import { useCallback, useEffect, useRef } from 'react';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    pollTimer: number;
  }
}

const usePolling = (poll: () => Promise<void>, interval = 10000) => {
  const isPollingEnabled = useRef(true);

  const queueNextPoll = useCallback(() => {
    if (!isPollingEnabled.current || document.hidden) {
      return;
    }

    const timeout = () =>
      window.setTimeout(async () => {
        if (!isPollingEnabled.current || document.hidden) {
          return;
        }
        await poll();
        queueNextPoll();
      }, interval);

    window.pollTimer = timeout();
  }, [poll, interval]);

  useEffect(() => {
    const onVisibilityChange = async () => {
      if (!isPollingEnabled.current || document.hidden) {
        return;
      }
      await poll();
      queueNextPoll();
    };
    window.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const setPollingEnabled = async (isEnabled: boolean) => {
    isPollingEnabled.current = isEnabled;
    if (!isPollingEnabled.current || document.hidden) {
      return;
    }
    await poll();
    queueNextPoll();
  };

  return { initializePolling: queueNextPoll, setPollingEnabled };
};

export default usePolling;
